import React from 'react';
import { getWebSocketManager } from '../utility/websocket';
/**
 * Generic hook to subscribe to a socket event with type-safe payload
 *
 * @example
 * ```tsx
 * type MyEvents = {
 *   'user-update': { userId: string, name: string }
 *   'notification': { message: string }
 * }
 *
 * useSocketEvent<MyEvents, 'user-update'>('user-update', (payload) => {
 *   console.log(payload.userId, payload.name)
 * })
 * ```
 */
export function useSocketEvent(event, handler) {
    const wsManager = React.useMemo(() => getWebSocketManager(), []);
    React.useEffect(() => {
        const unsubscribe = wsManager.on(event, (message) => {
            if (message.data) {
                handler(message.data);
            }
        });
        return unsubscribe;
    }, [wsManager, event, handler]);
}
/**
 * Generic hook to manually subscribe/unsubscribe to socket events
 * Returns a subscribe function that returns an unsubscribe function
 *
 * @example
 * ```tsx
 * const { subscribe } = useSocketSubscribe<MyEvents>()
 *
 * const unsubscribe = subscribe('user-update', (payload) => {
 *   console.log(payload.userId)
 * })
 *
 * // Later...
 * unsubscribe()
 * ```
 */
export function useSocketSubscribe() {
    const wsManager = React.useMemo(() => getWebSocketManager(), []);
    const subscribe = React.useCallback((event, handler) => {
        return wsManager.on(event, (message) => {
            if (message.data) {
                handler(message.data);
            }
        });
    }, [wsManager]);
    return { subscribe };
}
